import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Edit2, Trash2, Plus, Search, Check, X as XIcon } from 'lucide-react'
import { ColDef } from 'ag-grid-community'
import SidePanel from '@/components/ui/SidePanel'
import Tag from '@/components/ui/Tag'
import Button from '@/components/ui/Button'
import DataGrid from '@/components/data/DataGrid'
import { designTokens } from '@/utils/design-tokens'
import type { WorkspaceResponse } from '@/api/generated/models/WorkspaceResponse'
import type { DepartmentResponse } from '@/api/generated/models/DepartmentResponse'
import { DepartmentService } from '@/api/generated/services/DepartmentService'
import type { OrgMemberResponse } from '@/api/generated/models/OrgMemberResponse'
import { OrgMemberService } from '@/api/generated/services/OrgMemberService'
import type { DepartmentCreateRequest } from '@/api/generated/models/DepartmentCreateRequest'
import type { DepartmentUpdateRequest } from '@/api/generated/models/DepartmentUpdateRequest'
import DepartmentCreateModal from './DepartmentCreateModal'
import DepartmentEditModal from './DepartmentEditModal'
import MemberAddModal from './MemberAddModal'
import type { BusinessResponse } from '@/api/generated/models/BusinessResponse'
import { WorkspaceService } from '@/api/generated/services/WorkspaceService'
import { resolveMenuTree } from '@/components/ui/Sidebar/mergeMenuSettings'
import { getWorkspaceShowTasks } from '@/hooks/useShowTasks'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'
import { fetchBusinesses, createBusiness, updateBusiness, clearBusinesses, clearError } from '@/store/businessSlice'
import BusinessCreateModal from './BusinessCreateModal'
import BusinessEditModal from './BusinessEditModal'
import { AG_GRID_LOCALE_KR } from '@/locales/ag-grid-ko-KR.ts'

interface WorkspaceDetailPanelProps {
  isOpen: boolean
  onClose: () => void
  /** 표시할 워크스페이스 ID. null이면 패널 내용 비움 */
  workspaceId: string | null
  /** 부모가 수정 성공 후 이 값을 증가시키면 패널이 자체적으로 재조회 */
  refreshKey?: number
  /** 수정 버튼 클릭 시: 패널이 내부 로드한 WorkspaceResponse를 부모에 전달 */
  onEdit?: (workspace: WorkspaceResponse) => void
  /** 사이드바 메뉴 설정 "수정" 버튼 클릭 시 호출 (owner/admin만 노출) */
  onEditMenuSettings?: (workspace: WorkspaceResponse) => void
}

// Section Header Component
const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div 
    className="pb-2 border-b" 
    style={{ 
      borderBottomColor: '#EBEBEB',
      borderBottomWidth: '1px',
      padding: '7px 0px'
    }}
  >
    <h3 
      style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '18px',
        fontWeight: 700,
        color: designTokens.colors.gray[6]
      }}
    >
      {title}
    </h3>
  </div>
)

// Info Field Component
const InfoField: React.FC<{ 
  label: string
  value: string | React.ReactNode
  fullWidth?: boolean 
}> = ({ label, value, fullWidth = false }) => (
  <div className={`flex ${fullWidth ? 'flex-col gap-2' : 'items-center gap-5'}`}>
    <div className={fullWidth ? '' : 'w-37 flex-shrink-0'}>
      <span style={{
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '16px',
        fontWeight: 400,
        color: designTokens.colors.gray[6]
      }}>
        {label}
      </span>
    </div>
    <div className={fullWidth ? '' : 'flex-1 py-2'}>
      {typeof value === 'string' ? (
        <span style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '16px',
          fontWeight: 400,
          color: designTokens.colors.gray[6]
        }}>
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  </div>
)

const WorkspaceDetailPanel: React.FC<WorkspaceDetailPanelProps> = ({
  isOpen,
  onClose,
  workspaceId,
  refreshKey,
  onEdit,
  onEditMenuSettings,
}) => {
  const dispatch = useAppDispatch()

  // Redux state for businesses
  const { businesses, isLoading: isBusinessLoading, error: businessError } = useAppSelector(state => state.business)

  // 현재 사용자 role (사이드바 메뉴 설정 "수정" 버튼 가드 — 소유자만 수정할 수 있다)
  const orgMember = useAppSelector(state => state.auth.orgMember)
  const isOrgOwner = orgMember?.role === 'owner'

  // ===== 자체 데이터 로드 =====
  // workspace 단건 GET 응답에 menu_settings까지 포함되어 옴 — 별도 GET 없음.
  const [loadedWorkspace, setLoadedWorkspace] = useState<WorkspaceResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  // 사이드바 메뉴 설정은 loadedWorkspace.menu_settings에서 읽음
  const menuSettings = loadedWorkspace?.menu_settings ?? null

  // 데이터가 로딩 중인지 추적하는 상태 (자식 데이터 로딩 트랜지션)
  const [isInitializing, setIsInitializing] = useState(false)

  // Business state
  const [isBusinessCreateModalOpen, setIsBusinessCreateModalOpen] = useState(false)
  const [isBusinessEditModalOpen, setIsBusinessEditModalOpen] = useState(false)
  const [editingBusiness, setEditingBusiness] = useState<BusinessResponse | null>(null)

  // Department state
  const [departments, setDepartments] = useState<DepartmentResponse[]>([])
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false)
  const [isLoadingMoreDepartments, setIsLoadingMoreDepartments] = useState(false)
  const [departmentTotalItems, setDepartmentTotalItems] = useState(0)
  const [departmentHasNext, setDepartmentHasNext] = useState(false)
  const [departmentCurrentPage, setDepartmentCurrentPage] = useState(1)
  const [departmentPageSize, setDepartmentPageSize] = useState(20)

  const [members, setMembers] = useState<OrgMemberResponse[]>([])
  const [isLoadingMembers, setIsLoadingMembers] = useState(false)
  const [isLoadingMoreMembers, setIsLoadingMoreMembers] = useState(false)
  const [memberTotalItems, setMemberTotalItems] = useState(0)
  const [memberHasNext, setMemberHasNext] = useState(false)
  const [memberCurrentPage, setMemberCurrentPage] = useState(1)
  const [memberPageSize, setMemberPageSize] = useState(20)
  const [isMemberAddModalOpen, setIsMemberAddModalOpen] = useState(false)

  // Member search state
  const [memberSearchValue, setMemberSearchValue] = useState('')


  // Alert hook for user feedback
  const { showError, showSuccess } = useAlert()

  // Process Redux businessError through handleApiError for consistency
  useEffect(() => {
    if (businessError) {
      handleApiError(businessError, showError, ErrorMessages.BUSINESS_UPDATE_FAILED)
      // Clear the error immediately after processing to prevent repeated alerts
      dispatch(clearError())
    }
  }, [businessError, showError, dispatch])

  const [isDepartmentCreateModalOpen, setIsDepartmentCreateModalOpen] = useState(false)
  const [isDepartmentCreating, setIsDepartmentCreating] = useState(false)

  const [isDepartmentEditModalOpen, setIsDepartmentEditModalOpen] = useState(false)
  const [editingDepartment, setEditingDepartment] = useState<DepartmentResponse | null>(null)
  const [isDepartmentUpdating, setIsDepartmentUpdating] = useState(false)

  // 모든 상태를 완전히 초기화하는 함수
  const resetAllState = useCallback(() => {
    // Redux 상태 초기화
    dispatch(clearBusinesses())

    // 로컬 상태 초기화
    setDepartments([])
    setMembers([])
    setIsLoadingDepartments(false)
    setIsLoadingMoreDepartments(false)
    setDepartmentTotalItems(0)
    setDepartmentHasNext(false)
    setDepartmentCurrentPage(1)
    setIsLoadingMembers(false)
    setIsLoadingMoreMembers(false)
    setMemberTotalItems(0)
    setMemberHasNext(false)
    setMemberCurrentPage(1)

    // 모달 상태 초기화
    setIsBusinessCreateModalOpen(false)
    setIsBusinessEditModalOpen(false)
    setEditingBusiness(null)
    setIsDepartmentCreateModalOpen(false)
    setIsDepartmentEditModalOpen(false)
    setEditingDepartment(null)
    setIsDepartmentCreating(false)
    setIsDepartmentUpdating(false)
    setIsMemberAddModalOpen(false)

  }, [dispatch])

  // workspaceId/refreshKey 변경 시 단건 fetch.
  // - workspaceId가 바뀌면: 자식 데이터 전체 리셋 후 새 워크스페이스 fetch
  // - refreshKey만 바뀌면 (같은 워크스페이스의 부분 갱신, 예: menu_settings 저장 후):
  //   자식 데이터 reload는 생략하고 워크스페이스 본문만 재조회
  const prevWorkspaceIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isOpen || !workspaceId) {
      setLoadedWorkspace(null)
      setLoadError(null)
      return
    }
    const idChanged = prevWorkspaceIdRef.current !== workspaceId
    prevWorkspaceIdRef.current = workspaceId
    if (idChanged) {
      setIsInitializing(true)
      resetAllState()
      setTimeout(() => setIsInitializing(false), 100)
    }
    let cancelled = false
    setIsLoading(true)
    setLoadError(null)
    WorkspaceService.appsWorkspaceViewsGetWorkspace({ workspaceId })
      .then(res => {
        if (!cancelled) setLoadedWorkspace(res)
      })
      .catch(err => {
        if (cancelled) return
        console.error('Failed to load workspace detail:', err)
        handleApiError(err, showError, ErrorMessages.FETCH_FAILED)
        setLoadError('워크스페이스를 불러오지 못했습니다.')
        setLoadedWorkspace(null)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, workspaceId, refreshKey, resetAllState, showError])

  // 패널이 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      resetAllState()
      setLoadedWorkspace(null)
      setLoadError(null)
      setIsInitializing(false)
      prevWorkspaceIdRef.current = null
    }
  }, [isOpen, resetAllState])

  // 사업, 부서 및 멤버 데이터 로드
  useEffect(() => {
    if (loadedWorkspace && isOpen) {
      loadBusinesses()
      loadDepartments()
      loadMembers()
    }
  }, [loadedWorkspace, isOpen])


  const loadBusinesses = () => {
    if (!loadedWorkspace?.id) return

    dispatch(fetchBusinesses({
      workspaceId: loadedWorkspace.id,
      page: 1,
      perPage: 100,
      includeUnavailable: true
    }))
  }

  const loadDepartments = async (reset: boolean = true) => {
    if (!loadedWorkspace?.id) return

    if (reset) {
      setIsLoadingDepartments(true)
      setDepartments([])
      setDepartmentCurrentPage(1)
    } else {
      setIsLoadingMoreDepartments(true)
    }

    try {
      const pageToFetch = reset ? 1 : departmentCurrentPage + 1

      const response = await DepartmentService.appsDepartmentViewsListDepartments({
        workspaceId: loadedWorkspace.id,
        page: pageToFetch,
        perPage: departmentPageSize,
        includeUnavailable: true
      })

      if (response.data && response.meta) {
        const newDepartments = response.data as DepartmentResponse[]

        // Use meta fields for pagination info
        setDepartmentTotalItems(response.meta.total)
        setDepartmentHasNext(response.meta.has_next)
        setDepartmentCurrentPage(pageToFetch)

        if (reset) {
          // Create sparse array for initial load
          const sparseArray: any[] = []
          for (let i = 0; i < response.meta.total; i++) {
            if (i < newDepartments.length) {
              sparseArray.push(newDepartments[i])
            } else {
              sparseArray.push({
                id: `placeholder-dept-${i}`,
                isPlaceholder: true
              })
            }
          }
          setDepartments(sparseArray)
        } else {
          setDepartments(prev => [...prev, ...newDepartments])
        }
      }
    } catch (error) {
      console.error('Failed to load departments:', error)
      handleApiError(error, showError, ErrorMessages.FETCH_FAILED)
    } finally {
      setIsLoadingDepartments(false)
      setIsLoadingMoreDepartments(false)
    }
  }

  const fetchMoreDepartments = async () => {
    if (!loadedWorkspace?.id || !departmentHasNext || isLoadingMoreDepartments) {
      return
    }

    await loadDepartments(false)
  }

  // Load specific page directly (sparse loading)
  const loadDepartmentsPage = async (targetPage: number) => {
    if (!loadedWorkspace?.id || isLoadingMoreDepartments) {
      return
    }

    // Check if we already have this page's data
    const startIndex = (targetPage - 1) * departmentPageSize
    const endIndex = startIndex + departmentPageSize

    // Check if all items in this page range are real data (not placeholders)
    let hasAllData = true
    for (let i = startIndex; i < Math.min(endIndex, departments.length); i++) {
      if (!departments[i] || departments[i].isPlaceholder) {
        hasAllData = false
        break
      }
    }

    if (hasAllData && departments.length >= endIndex) {
      return // Already have the data
    }

    setIsLoadingMoreDepartments(true)

    try {
      const response = await DepartmentService.appsDepartmentViewsListDepartments({
        workspaceId: loadedWorkspace.id,
        page: targetPage,
        perPage: departmentPageSize,
        includeUnavailable: true
      })

      if (response.data && response.meta) {
        const newDepartments = response.data as DepartmentResponse[]

        // Create a sparse array that preserves existing data
        setDepartments(prev => {
          const updated = [...prev]

          // Ensure array is large enough
          const requiredLength = Math.max(updated.length, response.meta.total)
          while (updated.length < requiredLength) {
            updated.push({
              id: `placeholder-dept-${updated.length}`,
              isPlaceholder: true
            } as any)
          }

          // Insert the new page data at the correct position
          for (let i = 0; i < newDepartments.length; i++) {
            updated[startIndex + i] = newDepartments[i]
          }

          return updated
        })

        setDepartmentCurrentPage(targetPage)
        setDepartmentTotalItems(response.meta.total)
        setDepartmentHasNext(response.meta.has_next)
      }
    } catch (error) {
      console.error('Failed to load departments page:', error)
      handleApiError(error, showError, ErrorMessages.FETCH_FAILED)
    } finally {
      setIsLoadingMoreDepartments(false)
    }
  }

  const loadMembers = async (reset: boolean = true, searchQuery?: string) => {
    if (!loadedWorkspace?.org_id || !loadedWorkspace?.id) return

    if (reset) {
      setIsLoadingMembers(true)
      setMembers([])
      setMemberCurrentPage(1)
    } else {
      setIsLoadingMoreMembers(true)
    }

    try {
      const pageToFetch = reset ? 1 : memberCurrentPage + 1

      const response = await OrgMemberService.appsOrganizationViewsListOrgMembers({
        orgId: loadedWorkspace.org_id,
        workspaceId: loadedWorkspace.id,
        page: pageToFetch,
        perPage: memberPageSize,
        includeUnavailable: true,
        q: searchQuery
      })

      if (response.data && response.meta) {
        const newMembers = response.data as OrgMemberResponse[]

        // Use meta fields for pagination info
        setMemberTotalItems(response.meta.total)
        setMemberHasNext(response.meta.has_next)
        setMemberCurrentPage(pageToFetch)

        if (reset) {
          // Create sparse array for initial load
          const sparseArray: any[] = []
          for (let i = 0; i < response.meta.total; i++) {
            if (i < newMembers.length) {
              sparseArray.push(newMembers[i])
            } else {
              sparseArray.push({
                id: `placeholder-member-${i}`,
                isPlaceholder: true
              })
            }
          }
          setMembers(sparseArray)
        } else {
          setMembers(prev => [...prev, ...newMembers])
        }
      }
    } catch (error) {
      console.error('Failed to load members:', error)
      handleApiError(error, showError, ErrorMessages.FETCH_FAILED)
    } finally {
      setIsLoadingMembers(false)
      setIsLoadingMoreMembers(false)
    }
  }

  const fetchMoreMembers = async () => {
    if (!loadedWorkspace?.org_id || !loadedWorkspace?.id || !memberHasNext || isLoadingMoreMembers) {
      return
    }

    await loadMembers(false)
  }

  // Load specific page directly (sparse loading)
  const loadMembersPage = async (targetPage: number, searchQuery?: string) => {
    if (!loadedWorkspace?.org_id || !loadedWorkspace?.id || isLoadingMoreMembers) {
      return
    }

    // Check if we already have this page's data
    const startIndex = (targetPage - 1) * memberPageSize
    const endIndex = startIndex + memberPageSize

    // Check if all items in this page range are real data (not placeholders)
    let hasAllData = true
    for (let i = startIndex; i < Math.min(endIndex, members.length); i++) {
      if (!members[i] || members[i].isPlaceholder) {
        hasAllData = false
        break
      }
    }

    if (hasAllData && members.length >= endIndex) {
      return // Already have the data
    }

    setIsLoadingMoreMembers(true)

    try {
      const response = await OrgMemberService.appsOrganizationViewsListOrgMembers({
        orgId: loadedWorkspace.org_id,
        workspaceId: loadedWorkspace.id,
        page: targetPage,
        perPage: memberPageSize,
        includeUnavailable: true,
        q: searchQuery
      })

      if (response.data && response.meta) {
        const newMembers = response.data as OrgMemberResponse[]

        // Create a sparse array that preserves existing data
        setMembers(prev => {
          const updated = [...prev]

          // Ensure array is large enough
          const requiredLength = Math.max(updated.length, response.meta.total)
          while (updated.length < requiredLength) {
            updated.push({
              id: `placeholder-member-${updated.length}`,
              isPlaceholder: true
            } as any)
          }

          // Insert the new page data at the correct position
          for (let i = 0; i < newMembers.length; i++) {
            updated[startIndex + i] = newMembers[i]
          }

          return updated
        })

        setMemberCurrentPage(targetPage)
        setMemberTotalItems(response.meta.total)
        setMemberHasNext(response.meta.has_next)
      }
    } catch (error) {
      console.error('Failed to load members page:', error)
      handleApiError(error, showError, ErrorMessages.FETCH_FAILED)
    } finally {
      setIsLoadingMoreMembers(false)
    }
  }

  // Member search handler
  const handleMemberSearch = useCallback(async () => {
    if (!loadedWorkspace?.org_id || !loadedWorkspace?.id) return

    // Reset to first page and fetch with search query
    setMembers([])
    setMemberCurrentPage(1)
    await loadMembers(true, memberSearchValue.trim() || undefined)
  }, [loadedWorkspace?.org_id, loadedWorkspace?.id, memberSearchValue])

  // Clear member search handler
  const handleClearMemberSearch = useCallback(async () => {
    setMemberSearchValue('')

    if (!loadedWorkspace?.org_id || !loadedWorkspace?.id) return

    // Reset to first page and fetch without search query
    setMembers([])
    setMemberCurrentPage(1)
    await loadMembers(true, undefined)
  }, [loadedWorkspace?.org_id, loadedWorkspace?.id])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // 날짜 전용 포맷 (시작일/종료일 — 시각 없음)
  const formatDateOnly = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  const getStatusTag = (available: boolean) => (
    <Tag
      color={available ? "success" : "gray"}
      size="small"
    >
      ● {available ? '활성' : '비활성'}
    </Tag>
  )

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'owner':
        return '소유자'
      case 'admin':
        return '관리자'
      case 'normal':
        return '일반'
      default:
        return role
    }
  }

  const getRoleTag = (role: string) => {
    const roleText = getRoleDisplay(role)
    let color: "primary" | "secondary" | "success" | "error" | "warning" | "info" | "gray" | "white" | "light" | "dark"

    switch (role) {
      case 'owner':
        color = 'error'  // 소유자는 빨간색
        break
      case 'admin':
        color = 'warning'  // 관리자는 주황색
        break
      case 'normal':
        color = 'gray'  // 일반은 회색
        break
      default:
        color = 'gray'
    }

    return (
      <Tag color={color} size="small">
        {roleText}
      </Tag>
    )
  }

  const handleEditDepartment = async (departmentId: string) => {
    if (!loadedWorkspace?.id) {
      console.error('Cannot edit department: workspace not available')
      showError('워크스페이스 정보가 없어 부서를 수정할 수 없습니다.')
      return
    }

    try {

      // 부서 데이터를 직접 API에서 가져오기
      const response = await DepartmentService.appsDepartmentViewsListDepartments({
        workspaceId: loadedWorkspace.id,
        page: 1,
        perPage: 100,
        includeUnavailable: true
      })

      if (response.data) {
        const freshDepartments = response.data as DepartmentResponse[]
        const department = freshDepartments.find(d => d.id === departmentId)

        if (department) {
          setEditingDepartment(department)
          setIsDepartmentEditModalOpen(true)
        } else {
          showError('해당 부서를 찾을 수 없습니다.')
        }
      } else {
        showError('부서 데이터를 불러올 수 없습니다.')
      }
    } catch (error) {
      console.error('Failed to fetch department data:', error)
      handleApiError(error, showError, ErrorMessages.FETCH_FAILED)
    }
  }

  const handleDeleteMember = async (memberId: string) => {
    // 현재 패널에서 보고 있는 워크스페이스 정보 사용
    const currentWorkspace = loadedWorkspace

    if (!currentWorkspace?.id) {
      showError('워크스페이스 정보가 없어 멤버를 삭제할 수 없습니다.')
      return
    }

    // Find member name for confirmation
    const member = members.find(m => m.id === memberId)
    const memberName = member?.account_name || '알 수 없는 멤버'

    // Show confirmation dialog
    const confirmed = window.confirm(
      `"${memberName}" 멤버를 워크스페이스에서 제거하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`
    )

    if (!confirmed) {
      return
    }

    try {
      await OrgMemberService.appsOrganizationViewsOrgMemberRemoveWorkspace({
        memberId: memberId,
        workspaceId: currentWorkspace.id
      })

      showSuccess(`"${memberName}" 멤버가 워크스페이스에서 제거되었습니다.`)

      // Refresh member list
      loadMembers()
    } catch (error: any) {
      console.error('Failed to remove member from workspace:', error)

      handleApiError(error, showError, '멤버 제거에 실패했습니다')
    }
  }

  const handleCreateDepartment = async (departmentData: { name: string; sortKey?: number }) => {
    if (!loadedWorkspace?.id) {
      console.error('Cannot create department: workspace not available')
      showError('워크스페이스 정보가 없어 부서를 생성할 수 없습니다.')
      return
    }

    setIsDepartmentCreating(true)

    try {
      const createRequest: DepartmentCreateRequest = {
        workspace_id: loadedWorkspace.id,
        name: departmentData.name,
        sort_key: departmentData.sortKey !== undefined ? departmentData.sortKey : null
      }

      await DepartmentService.appsDepartmentViewsCreateDepartment({
        requestBody: createRequest
      })

      // 성공 시 부서 목록 새로고침
      loadDepartments()
      setIsDepartmentCreateModalOpen(false)
      showSuccess('부서가 생성되었습니다.')
    } catch (error) {
      console.error('Failed to create department:', error)
      handleApiError(error, showError, ErrorMessages.CREATE_FAILED)
    } finally {
      setIsDepartmentCreating(false)
    }
  }

  const handleUpdateDepartment = async (updateData: { name: string; sortKey?: number; available: boolean }) => {
    if (!editingDepartment?.id) {
      console.error('Cannot update department: no department selected for editing')
      showError('수정할 부서가 선택되지 않았습니다.')
      return
    }

    setIsDepartmentUpdating(true)

    try {
      const updateRequest: DepartmentUpdateRequest = {
        name: updateData.name,
        sort_key: updateData.sortKey !== undefined ? updateData.sortKey : null,
        available: updateData.available
      }

      await DepartmentService.appsDepartmentViewsUpdateDepartment({
        departmentId: editingDepartment.id,
        requestBody: updateRequest
      })

      // 성공 시 부서 목록 새로고침 및 모달 닫기
      loadDepartments()
      handleEditModalClose()
    } catch (error) {
      console.error('Failed to update department:', error)
      handleApiError(error, showError, ErrorMessages.UPDATE_FAILED)
    } finally {
      setIsDepartmentUpdating(false)
    }
  }

  const handleEditModalClose = () => {
    setIsDepartmentEditModalOpen(false)
    setEditingDepartment(null)
  }

  // Business handlers
  const handleCreateBusiness = async (businessData: {
    name: string;
    sortKey?: number;
    labels?: Array<{key: string; name: string}>;
    detail_fields?: Array<{key: string; name: string}>;
    extra_fields?: Array<{key: string; name: string}>;
  }) => {
    if (!loadedWorkspace?.id) {
      console.error('Cannot create business: workspace not available')
      return
    }

    // Prevent multiple simultaneous requests
    if (isBusinessLoading) {
      return
    }

    try {
      await dispatch(createBusiness({
        name: businessData.name,
        workspace_id: loadedWorkspace.id,
        sort_key: businessData.sortKey,
        labels: businessData.labels,
        detail_fields: businessData.detail_fields,
        extra_fields: businessData.extra_fields
      })).unwrap()

      // Close modal on success
      setIsBusinessCreateModalOpen(false)
      showSuccess('사업이 생성되었습니다.')

      // Refresh business list
      loadBusinesses()
    } catch (error) {
      console.error('Failed to create business:', error)
      // Use common error handler
      handleApiError(error, showError, ErrorMessages.BUSINESS_CREATE_FAILED)
      // Keep modal open on error so user can retry
    }
  }

  const handleEditBusiness = (businessId: string) => {
    const business = businesses.find(b => b.id === businessId)
    if (business) {
      setEditingBusiness(business)
      setIsBusinessEditModalOpen(true)
    } else {
      console.error('Business not found with ID:', businessId)
    }
  }

  const handleEditBusinessSubmit = async (businessData: {
    name: string;
    sortKey?: number;
    available: boolean;
    labels?: Array<{id?: string; key: string}>;
    detail_fields?: Array<{id?: string; key: string}>;
    extra_fields?: Array<{id?: string; key: string}>;
    report_settings?: Record<string, { sort_key: number; ignored: boolean }>;
  }) => {
    if (!editingBusiness?.id || !loadedWorkspace?.id) {
      console.error('No business selected for editing or no workspace selected')
      return
    }

    try {
      await dispatch(updateBusiness({
        businessId: editingBusiness.id,
        data: {
          name: businessData.name,
          sort_key: businessData.sortKey,
          available: businessData.available,
          labels: businessData.labels,
          detail_fields: businessData.detail_fields,
          extra_fields: businessData.extra_fields,
          report_settings: businessData.report_settings
        }
      })).unwrap()

      // Close modal on success
      setIsBusinessEditModalOpen(false)
      setEditingBusiness(null)

      // Refresh business list
      loadBusinesses()
    } catch (error) {
      console.error('Failed to update business:', error)
      // Keep modal open on error so user can retry
    }
  }

  const handleBusinessEditModalClose = () => {
    setIsBusinessEditModalOpen(false)
    setEditingBusiness(null)
  }


  const handleMemberAddSuccess = () => {
    // Refresh member list after successful addition
    loadMembers()
    setIsMemberAddModalOpen(false)
    showSuccess('멤버가 초대되었습니다.')
  }

  // 사업 DataGrid 컬럼 정의
  const businessColumnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '순서',
      field: 'sort_key',
      width: 50,
      filter: false,
      sortable: false,
    },
    {
      headerName: '상태',
      field: 'available',
      width: 80,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => (
        <div className="flex items-center h-full">
          <Tag
            color={params.value ? "success" : "gray"}
            size="small"
          >
            ● {params.value ? '활성' : '비활성'}
          </Tag>
        </div>
      )
    },
    {
      headerName: '이름',
      field: 'name',
      flex: 1,
      minWidth: 230,
      filter: false,
      sortable: false,
    },
    {
      headerName: '액션',
      field: 'actions',
      width: 100,
      cellRenderer: (params: any) => {
        return (
          <div className="flex items-center h-full gap-2">
            <Button
              size="small"
              color="white"
              onClick={() => handleEditBusiness(params.data.id)}
              className="flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" />
              수정
            </Button>
          </div>
        )
      },
      sortable: false,
      filter: false
    }
  ], [handleEditBusiness])

  // 부서 DataGrid 컬럼 정의
  const departmentColumnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '순서',
      field: 'sort_key',
      width: 100,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded w-12"></div>
        }
        return params.value || '-'
      }
    },
    {
      headerName: '상태',
      field: 'available',
      width: 120,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        // Handle placeholder rows
        if (params.data?.isPlaceholder) {
          return <div className="h-full bg-gray-50 animate-pulse"></div>
        }

        if (params.value === undefined || params.value === null) {
          return null
        }
        return (
          <div className="flex items-center h-full">
            <Tag
              color={params.value ? "success" : "gray"}
              size="small"
            >
              ● {params.value ? '활성' : '비활성'}
            </Tag>
          </div>
        )
      }
    },
    {
      headerName: '이름',
      field: 'name',
      flex: 1,
      minWidth: 200,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      }
    },
    {
      headerName: '액션',
      field: 'actions',
      width: 140,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        // Handle placeholder rows
        if (params.data?.isPlaceholder) {
          return <div className="h-8 bg-gray-50 animate-pulse rounded w-16"></div>
        }

        if (!params.data) {
          return null
        }

        return (
          <div className="flex items-center h-full gap-2">
            <Button
              size="small"
              color="white"
              onClick={() => handleEditDepartment(params.data.id)}
              className="flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" />
              수정
            </Button>
          </div>
        )
      }
    }
  ], [handleEditDepartment])

  // 멤버 DataGrid 컬럼 정의
  const memberColumnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '상태',
      field: 'available',
      width: 120,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        // Handle placeholder rows
        if (params.data?.isPlaceholder) {
          return <div className="h-full bg-gray-50 animate-pulse"></div>
        }

        if (params.value === undefined || params.value === null) {
          return null
        }
        return (
          <div className="flex items-center h-full">
            <Tag
              color={params.value ? "success" : "gray"}
              size="small"
            >
              ● {params.value ? '활성' : '비활성'}
            </Tag>
          </div>
        )
      }
    },
    {
      headerName: '이름',
      field: 'account_name',
      flex: 1,
      minWidth: 150,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      }
    },
    {
      headerName: '역할',
      field: 'role',
      width: 100,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded w-12"></div>
        }
        if (!params.value) {
          return null
        }
        return (
          <div className="flex items-center h-full">
            {getRoleTag(params.value)}
          </div>
        )
      }
    },
    {
      headerName: '이메일',
      field: 'account_email',
      flex: 2,
      minWidth: 200,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        if (params.data?.isPlaceholder) {
          return <div className="h-6 bg-gray-50 animate-pulse rounded"></div>
        }
        return params.value || '-'
      }
    },
    {
      headerName: '액션',
      field: 'actions',
      width: 80,
      filter: false,
      sortable: false,
      cellRenderer: (params: any) => {
        // Handle placeholder rows
        if (params.data?.isPlaceholder) {
          return <div className="h-8 bg-gray-50 animate-pulse rounded w-16"></div>
        }

        // 데이터 검증 추가
        if (!params.data) {
          return null
        }

        return (
          <div className="flex items-center h-full gap-2">
            <Button
              size="small"
              color="light"
              onClick={() => handleDeleteMember(params.data.id)}
              className="flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" />
              삭제
            </Button>
          </div>
        )
      }
    }
  ], [handleDeleteMember])

  // members array already contains placeholders from sparse loading
  // No need to create additional virtual rows

  // departments array already contains placeholders from sparse loading
  // No need to create additional virtual rows

  const headerActions = (
    <Button
      size="small"
      color="dark"
      onClick={() => loadedWorkspace && onEdit?.(loadedWorkspace)}
      className="flex items-center gap-1"
      disabled={!loadedWorkspace}
    >
      <Edit2 className="w-4 h-4" />
      수정
    </Button>
  )

  // 로딩/에러/빈 상태 → 패널은 열린 채로 placeholder
  if (!loadedWorkspace || isInitializing) {
    return (
      <SidePanel
        isOpen={isOpen}
        onClose={onClose}
        title="워크스페이스 상세"
        width={800}
      >
        <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
          {loadError ? (
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.error.DEFAULT,
            }}>
              {loadError}
            </span>
          ) : isLoading || isInitializing ? (
            <div className="flex flex-col items-center gap-3">
              <div
                className="animate-spin rounded-full h-8 w-8 border-b-2"
                style={{ borderColor: designTokens.colors.primary.DEFAULT }}
              />
              <span style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                color: designTokens.colors.gray[5],
              }}>
                데이터를 불러오는 중...
              </span>
            </div>
          ) : (
            <span style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[4],
            }}>
              표시할 항목이 없습니다.
            </span>
          )}
        </div>
      </SidePanel>
    )
  }

  return (
    <>
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="워크스페이스 상세"
      width={800}
      headerActions={headerActions}
    >
      <div className="px-6 py-6 space-y-8 overflow-y-auto flex-1">
        {/* 기본 정보 섹션 */}
        <div className="space-y-6">
          <SectionHeader title="기본 정보" />
          <div className="space-y-4">
            <InfoField
              label="워크스페이스명"
              value={loadedWorkspace.name}
            />
            <InfoField
              label="상태"
              value={getStatusTag(loadedWorkspace.available)}
            />
            <InfoField
              label="순서"
              value={loadedWorkspace.sort_key?.toString() || '-'}
            />
            <InfoField
              label="세부실행과제 표시"
              value={
                <Tag color={getWorkspaceShowTasks(loadedWorkspace) ? 'success' : 'gray'} size="small">
                  ● {getWorkspaceShowTasks(loadedWorkspace) ? 'ON' : 'OFF'}
                </Tag>
              }
            />
            <InfoField
              label="시작일"
              value={loadedWorkspace.start_date ? formatDateOnly(loadedWorkspace.start_date) : '-'}
            />
            <InfoField
              label="종료일"
              value={loadedWorkspace.end_date ? formatDateOnly(loadedWorkspace.end_date) : '-'}
            />
            <InfoField
              label="생성일시"
              value={loadedWorkspace.created_at ? formatDate(loadedWorkspace.created_at) : '-'}
            />
            {loadedWorkspace.updated_at && (
              <InfoField
                label="수정일시"
                value={formatDate(loadedWorkspace.updated_at)}
              />
            )}
          </div>
        </div>

        {/* 사업 섹션 */}
        <div className="space-y-6">
          <div
            className="pb-2 border-b flex items-center justify-between"
            style={{
              borderBottomColor: '#EBEBEB',
              borderBottomWidth: '1px',
              padding: '7px 0px'
            }}
          >
            <h3
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 700,
                color: designTokens.colors.gray[6]
              }}
            >
              사업
            </h3>
            <Button
              size="small"
              color="dark"
              onClick={() => setIsBusinessCreateModalOpen(true)}
              className="flex items-center gap-1"
              disabled={!loadedWorkspace?.id}
            >
              <Plus className="w-3 h-3" />
              생성
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200">
            {isBusinessLoading ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">사업 데이터를 불러오는 중...</p>
              </div>
            ) : businesses.length === 0 ? (
              <div className="flex items-center justify-center h-24">
                <p className="text-gray-500">등록된 사업이 없습니다.</p>
              </div>
            ) : (
              <DataGrid
                columnDefs={businessColumnDefs}
                rowData={businesses}
                headerHeight={40}
                rowHeight={42}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50]}
                height={40 + (10 * 42) + 60 + 2}
                rowSelection="single"
                getRowStyle={(params) => {
                  // 비활성화된 사업의 경우 시각적으로 구분
                  if (!params.data.available) {
                    return {
                      opacity: 0.6,
                      backgroundColor: '#f9f9f9'
                    }
                  }
                  return undefined
                }}
              />
            )}
          </div>
        </div>

        {/* 부서 섹션 */}
        <div className="space-y-6">
          <div 
            className="pb-2 border-b flex items-center justify-between" 
            style={{ 
              borderBottomColor: '#EBEBEB',
              borderBottomWidth: '1px',
              padding: '7px 0px'
            }}
          >
            <h3 
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 700,
                color: designTokens.colors.gray[6]
              }}
            >
              부서
            </h3>
            <Button
              size="small"
              color="dark"
              onClick={() => setIsDepartmentCreateModalOpen(true)}
              className="flex items-center gap-1"
              disabled={!loadedWorkspace?.id}
            >
              <Plus className="w-3 h-3" />
              생성
            </Button>
          </div>
          <div className="bg-white rounded-lg border border-gray-200">
            {isLoadingDepartments ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">부서 데이터를 불러오는 중...</p>
              </div>
            ) : departments.length === 0 ? (
              <div className="flex items-center justify-center h-24">
                <p className="text-gray-500">등록된 부서가 없습니다.</p>
              </div>
            ) : (
              <DataGrid
                columnDefs={departmentColumnDefs}
                rowData={departments}
                // Use client-side pagination with manual total count override
                rowModelType="clientSide"
                pagination={true}
                paginationPageSize={departmentPageSize}
                paginationPageSizeSelector={[20, 50, 100]}
                // Suppress built-in pagination panel to use custom one
                suppressPaginationPanel={false}
                // Override pagination info to show server total
                localeText={{
                  ...AG_GRID_LOCALE_KR,
                  // Custom pagination text showing server total
                  paginationOf: `총 ${departmentTotalItems.toLocaleString()}개`,
                  page: '페이지',
                  to: '-',
                  of: `중`
                }}
                // Pagination changed handler for incremental loading
                onPaginationChanged={async (params: any) => {
                  const api = params.api
                  const currentPage = api.paginationGetCurrentPage() + 1 // AG-Grid uses 0-based
                  const currentPageSize = api.paginationGetPageSize()

                  // Handle page size changes
                  if (currentPageSize !== departmentPageSize && loadedWorkspace?.id) {
                    setDepartmentPageSize(currentPageSize)
                    setDepartmentCurrentPage(1)
                    setDepartments([])
                    await loadDepartments(true)
                    return
                  }

                  // Prevent loading if already in progress
                  if (isLoadingMoreDepartments) {
                    return
                  }

                  // Handle page navigation - check if we need more data
                  const startIndex = (currentPage - 1) * departmentPageSize
                  const endIndex = startIndex + departmentPageSize
                  const actualEndIndex = Math.min(endIndex, departmentTotalItems)

                  // Check if current page has placeholder data
                  let needsLoad = false
                  if (actualEndIndex > departments.length) {
                    needsLoad = true
                  } else {
                    // Check if any item in the current page is a placeholder
                    for (let i = startIndex; i < actualEndIndex; i++) {
                      if (!departments[i] || departments[i].isPlaceholder) {
                        needsLoad = true
                        break
                      }
                    }
                  }

                  // Load data if we're viewing placeholder rows
                  if (needsLoad && loadedWorkspace?.id) {
                    // Load only the specific page needed (sparse loading)
                    await loadDepartmentsPage(currentPage)
                  }
                }}
                // Override total row count for pagination calculation
                paginationNumberFormatter={(params: any) => {
                  // This ensures AG-Grid calculates pagination based on server total
                  return params.value.toLocaleString()
                }}
                // Row ID and styling
                getRowId={(params: any) => {
                  if (!params.data) {
                    return null
                  }
                  const id = params.data.id
                  return id
                }}
                headerHeight={40}
                rowHeight={42}
                height={40 + (20 * 42) + 60 + 2}
                rowSelection="single"
                getRowStyle={(params) => {
                  // 데이터가 없으면 기본 스타일 반환
                  if (!params.data) {
                    return undefined
                  }

                  // 비활성화된 부서의 경우 시각적으로 구분
                  if (!params.data.available) {
                    return {
                      opacity: 0.6,
                      backgroundColor: '#f9f9f9'
                    }
                  }
                  return undefined
                }}
                // Loading state
                loading={isLoadingDepartments || isLoadingMoreDepartments}
              />
            )}
          </div>
        </div>

        {/* 멤버 섹션 */}
        <div className="space-y-6">
          {/* Member Section Header with Search - Single Row */}
          <div
            className="pb-2 border-b flex items-center justify-between gap-3"
            style={{
              borderBottomColor: '#EBEBEB',
              borderBottomWidth: '1px',
              padding: '7px 0px'
            }}
          >
            {/* Title */}
            <h3
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 700,
                color: designTokens.colors.gray[6]
              }}
            >
              멤버
            </h3>

            {/* Search and Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Search Input */}
              <div className="relative w-[240px]">
                <div
                  className="flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded transition-all duration-150"
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderColor: '#EBEBEB'
                  }}
                >
                  <Search
                    className="w-5 h-5"
                    style={{ color: '#B8B8B8' }}
                  />
                  <input
                    type="text"
                    placeholder="이름, 이메일로 검색"
                    value={memberSearchValue}
                    onChange={(e) => setMemberSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleMemberSearch()
                      }
                    }}
                    className="flex-1 bg-transparent outline-none"
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '16px',
                      lineHeight: '1.193359375em',
                      color: memberSearchValue ? designTokens.colors.gray[6] : designTokens.colors.gray[4]
                    }}
                  />
                  {memberSearchValue && (
                    <button
                      onClick={handleClearMemberSearch}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="검색어 지우기"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Search Button */}
              <Button
                size="small"
                color="dark"
                onClick={handleMemberSearch}
                className="h-10"
              >
                검색
              </Button>

              {/* Add Member Button */}
              <Button
                size="small"
                color="dark"
                onClick={() => setIsMemberAddModalOpen(true)}
                className="flex items-center gap-1"
                disabled={!loadedWorkspace?.id}
              >
                <Plus className="w-3 h-3" />
                추가
              </Button>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200">
            {isLoadingMembers ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">멤버 데이터를 불러오는 중...</p>
              </div>
            ) : members.length === 0 ? (
              <div className="flex items-center justify-center h-24">
                <p className="text-gray-500">등록된 멤버가 없습니다.</p>
              </div>
            ) : (
              <DataGrid
                columnDefs={memberColumnDefs}
                rowData={members}
                // Use client-side pagination with manual total count override
                rowModelType="clientSide"
                pagination={true}
                paginationPageSize={memberPageSize}
                paginationPageSizeSelector={[20, 50, 100]}
                // Suppress built-in pagination panel to use custom one
                suppressPaginationPanel={false}
                // Override pagination info to show server total
                localeText={{
                  ...AG_GRID_LOCALE_KR,
                  // Custom pagination text showing server total
                  paginationOf: `총 ${memberTotalItems.toLocaleString()}명`,
                  page: '페이지',
                  to: '-',
                  of: `중`
                }}
                // Pagination changed handler for incremental loading
                onPaginationChanged={async (params: any) => {
                  const api = params.api
                  const currentPage = api.paginationGetCurrentPage() + 1 // AG-Grid uses 0-based
                  const currentPageSize = api.paginationGetPageSize()

                  // Handle page size changes
                  if (currentPageSize !== memberPageSize && loadedWorkspace?.org_id && loadedWorkspace?.id) {
                    setMemberPageSize(currentPageSize)
                    setMemberCurrentPage(1)
                    setMembers([])
                    await loadMembers(true)
                    return
                  }

                  // Prevent loading if already in progress
                  if (isLoadingMoreMembers) {
                    return
                  }

                  // Handle page navigation - check if we need more data
                  const startIndex = (currentPage - 1) * memberPageSize
                  const endIndex = startIndex + memberPageSize
                  const actualEndIndex = Math.min(endIndex, memberTotalItems)

                  // Check if current page has placeholder data
                  let needsLoad = false
                  if (actualEndIndex > members.length) {
                    needsLoad = true
                  } else {
                    // Check if any item in the current page is a placeholder
                    for (let i = startIndex; i < actualEndIndex; i++) {
                      if (!members[i] || members[i].isPlaceholder) {
                        needsLoad = true
                        break
                      }
                    }
                  }

                  // Load data if we're viewing placeholder rows
                  if (needsLoad && loadedWorkspace?.org_id && loadedWorkspace?.id) {
                    // Load only the specific page needed (sparse loading)
                    // Pass current search query to maintain search context during pagination
                    await loadMembersPage(currentPage, memberSearchValue.trim() || undefined)
                  }
                }}
                // Override total row count for pagination calculation
                paginationNumberFormatter={(params: any) => {
                  // This ensures AG-Grid calculates pagination based on server total
                  return params.value.toLocaleString()
                }}
                // Row ID and styling
                getRowId={(params: any) => {
                  if (!params.data) {
                    return null
                  }
                  const id = params.data.id
                  return id
                }}
                headerHeight={40}
                rowHeight={42}
                height={40 + (20 * 42) + 60 + 2}
                rowSelection="single"
                getRowStyle={(params) => {
                  // 데이터가 없으면 기본 스타일 반환
                  if (!params.data) {
                    return undefined
                  }

                  // 비활성화된 멤버의 경우 시각적으로 구분
                  if (!params.data.available) {
                    return {
                      opacity: 0.6,
                      backgroundColor: '#f9f9f9'
                    }
                  }
                  return undefined
                }}
                // Loading state
                loading={isLoadingMembers || isLoadingMoreMembers}
              />
            )}
          </div>
        </div>

        {/* 사이드바 메뉴 설정 섹션 */}
        <div className="space-y-6">
          <div
            className="pb-2 border-b flex items-center justify-between"
            style={{
              borderBottomColor: '#EBEBEB',
              borderBottomWidth: '1px',
              padding: '7px 0px',
            }}
          >
            <h3
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '18px',
                fontWeight: 700,
                color: designTokens.colors.gray[6],
              }}
            >
              사이드바 메뉴 설정
            </h3>
            {isOrgOwner && (
              <Button
                size="small"
                color="dark"
                onClick={() => loadedWorkspace && onEditMenuSettings?.(loadedWorkspace)}
                className="flex items-center gap-1"
                disabled={!loadedWorkspace?.id}
              >
                <Edit2 className="w-3 h-3" />
                수정
              </Button>
            )}
          </div>

          <div className="space-y-2">
            {/* hasAdminPrivileges 기준으로 미리보기 — 일반 멤버에게는 adminOnly 항목이 숨김 처리되어 카탈로그상 다르게 보일 수 있음.
                여기서는 워크스페이스 관리자 시점이므로 hasAdminPrivileges=true로 전체 표시. */}
            {resolveMenuTree(menuSettings, true).map(({ parent, children }) => {
                const parentDimmed = !parent.isActive
                return (
                  <div key={parent.catalog.key} className="space-y-1">
                    {/* 1depth */}
                    <div
                      className="flex items-center gap-2 py-1"
                      style={{ opacity: parentDimmed ? 0.45 : 1 }}
                    >
                      {parent.isActive ? (
                        <Check className="w-4 h-4" style={{ color: designTokens.colors.success.DEFAULT }} />
                      ) : (
                        <XIcon className="w-4 h-4" style={{ color: designTokens.colors.gray[4] }} />
                      )}
                      <span style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '15px',
                        fontWeight: 600,
                        color: designTokens.colors.gray[6],
                      }}>
                        {parent.catalog.label}
                      </span>
                    </div>
                    {/* 2depth */}
                    <div className="space-y-1 pl-6">
                      {children.map(child => {
                        const dimmed = parentDimmed || !child.isActive
                        return (
                          <div
                            key={child.catalog.key}
                            className="flex items-center gap-2 py-0.5"
                            style={{ opacity: dimmed ? 0.45 : 1 }}
                          >
                            {child.isActive ? (
                              <Check className="w-3.5 h-3.5" style={{ color: designTokens.colors.success.DEFAULT }} />
                            ) : (
                              <XIcon className="w-3.5 h-3.5" style={{ color: designTokens.colors.gray[4] }} />
                            )}
                            <span style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              fontSize: '14px',
                              fontWeight: 400,
                              color: designTokens.colors.gray[6],
                            }}>
                              {child.catalog.label}
                            </span>
                            {child.catalog.adminOnly && (
                              <span style={{
                                fontFamily: designTokens.typography.fontFamily.sans[0],
                                fontSize: '12px',
                                color: designTokens.colors.gray[4],
                              }}>
                                (관리자 전용)
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
          </div>
        </div>
      </div>
    </SidePanel>

    {/* Business Create Modal */}
    <BusinessCreateModal
      isOpen={isBusinessCreateModalOpen}
      onClose={() => setIsBusinessCreateModalOpen(false)}
      onSubmit={handleCreateBusiness}
      isLoading={isBusinessLoading}
    />

    {/* Business Edit Modal */}
    <BusinessEditModal
      isOpen={isBusinessEditModalOpen}
      onClose={handleBusinessEditModalClose}
      onSubmit={handleEditBusinessSubmit}
      business={editingBusiness}
      isLoading={isBusinessLoading}
    />

    {/* Department Create Modal */}
    <DepartmentCreateModal
      isOpen={isDepartmentCreateModalOpen}
      onClose={() => setIsDepartmentCreateModalOpen(false)}
      onSubmit={handleCreateDepartment}
      isLoading={isDepartmentCreating}
    />

    {/* Department Edit Modal */}
    <DepartmentEditModal
      isOpen={isDepartmentEditModalOpen}
      onClose={handleEditModalClose}
      onSubmit={handleUpdateDepartment}
      department={editingDepartment}
      isLoading={isDepartmentUpdating}
    />

    {/* Member Add Modal */}
    <MemberAddModal
      isOpen={isMemberAddModalOpen}
      onClose={() => setIsMemberAddModalOpen(false)}
      onSuccess={handleMemberAddSuccess}
      workspaceId={loadedWorkspace?.id || ''}
      orgId={loadedWorkspace?.org_id || ''}
    />

  </>
  )
}

export default WorkspaceDetailPanel